import type { DataProvider } from "ra-core";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { DirRow } from "./DirectoryExplorer";
import {
  findNearbyStops,
  NEARBY_FETCH_LIMIT,
  type NearbyExclusions,
} from "./nearbyRound";
import type { RoutePoint } from "./nearestNeighborRoute";

/** Empresas ya visitadas (nota con canal 'visita'); RLS limita a tu workspace. */
export const fetchVisitedCompanyIds = async (): Promise<Set<number>> => {
  const sb = getSupabaseClient();
  // ponytail: PostgREST corta en 1000 filas; si hay más visitas, paginar.
  const { data: notes, error } = await sb
    .from("contact_notes")
    .select("contact_id")
    .eq("channel", "visita");
  if (error) throw new Error(error.message);
  const contactIds = [...new Set((notes ?? []).map((n) => n.contact_id))];
  if (contactIds.length === 0) return new Set();
  const { data: contacts, error: err2 } = await sb
    .from("contacts")
    .select("company_id")
    .in("id", contactIds);
  if (err2) throw new Error(err2.message);
  return new Set(
    (contacts ?? [])
      .map((c) => c.company_id as number | null)
      .filter((id): id is number => id != null),
  );
};

/**
 * Paradas cerca de `center` bajo el filtro activo del Directorio (`filterValues`, el
 * mismo que traduce `toPostgrest` en el dataProvider). Excluye descartadas
 * permanentemente (`discarded_at`), empresas con visita y `excludeIds`.
 */
export const fetchNearbyStops = async (opts: {
  dataProvider: DataProvider;
  filter: Record<string, unknown>;
  center: RoutePoint;
  n: number;
  excludeIds?: ReadonlySet<number>;
}) => {
  const exclude: NearbyExclusions = {
    ids: opts.excludeIds,
    companyIds: await fetchVisitedCompanyIds(),
  };
  const rows = await findNearbyStops({
    center: opts.center,
    n: opts.n,
    exclude,
    fetchBox: async (box) => {
      // Orden por termómetro (no por id): el dataProvider solo traduce los
      // filtros de pantalla a PostgREST cuando el orden no es por id.
      const { data } = await opts.dataProvider.getList<DirRow>("directory", {
        filter: { ...opts.filter, ...box, "discarded_at@is": "null" },
        sort: { field: "thermo_base", order: "DESC" },
        pagination: { page: 1, perPage: NEARBY_FETCH_LIMIT },
      });
      return data.map((r) => ({
        id: r.id,
        lat: r.lat,
        lng: r.lng,
        nombre: r.nombre_comercial ?? r.razon_social ?? `Empresa ${r.id}`,
        thermo_base: r.thermo_base,
        company_id: r.company_id,
      }));
    },
  });
  return rows;
};
